import { ChangeEvent, forwardRef, SyntheticEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactCrop, { type Crop, type PixelCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/Domain/components/Shared/Dialog/Dialog';

const ASPECT_RATIO = 1;
const MIN_DIMENSION = 300;
const DEFAULT_MAX_SIZE = 5 * 1024 * 1024;
const ACCEPT_EXTENSIONS = 'image/jpeg,image/png,image/webp';

const setCanvasPreview = (
  image: HTMLImageElement | null,
  canvas: HTMLCanvasElement | null,
  crop: PixelCrop,
) => {
  if (image == null || canvas == null) {
    throw new Error('Image or canvas is null');
  }

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('No 2d context');
  }

  const pixelRatio = window.devicePixelRatio;

  const maxWidth = 800;
  const maxHeight = 800;

  let newWidth = crop.width;
  let newHeight = crop.height;

  if (newWidth > maxWidth) {
    newHeight *= maxWidth / newWidth;
    newWidth = maxWidth;
  }

  if (newHeight > maxHeight) {
    newWidth *= maxHeight / newHeight;
    newHeight = maxHeight;
  }

  canvas.width = newWidth * pixelRatio;
  canvas.height = newHeight * pixelRatio;

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = 'high';

  ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, newWidth, newHeight);
};

const convertToPixelCrop = (crop: Crop, imageElement: HTMLImageElement): PixelCrop => {
  let x, y, width, height;

  if (crop.unit === 'px') {
    const scaleX = imageElement.naturalWidth / imageElement.width;
    const scaleY = imageElement.naturalHeight / imageElement.height;

    x = Math.round(crop.x * scaleX);
    y = Math.round(crop.y * scaleY);
    width = Math.round(crop.width * scaleX);
    height = Math.round(crop.height * scaleY);
  } else {
    // crop.unit === '%'
    x = Math.round((crop.x * imageElement.naturalWidth) / 100);
    y = Math.round((crop.y * imageElement.naturalHeight) / 100);
    width = Math.round((crop.width * imageElement.naturalWidth) / 100);
    height = Math.round((crop.height * imageElement.naturalHeight) / 100);
  }

  return {
    unit: 'px',
    x,
    y,
    width,
    height,
  };
};

type Props = {
  onChange: (data: string) => void;
  onError: (error: Error) => void;
  maxSize?: number;
  accept?: string;
  className?: string;
};

interface FormData {
  image: FileList;
}

const ImageInput = forwardRef<HTMLInputElement, Props>(
  (
    { onChange, onError, className, maxSize = DEFAULT_MAX_SIZE, accept = ACCEPT_EXTENSIONS },
    ref,
  ) => {
    const { t } = useTranslation(['crop_image', 'common']);

    const imgRef = useRef<HTMLImageElement>(null);
    const previewCanvasRef = useRef<HTMLCanvasElement>(null);

    const [imgSrc, setImgSrc] = useState<string>('');
    const [crop, setCrop] = useState<Crop>();
    const [error, setError] = useState<string>('');
    const [show, setShow] = useState<boolean>(false);

    const onSelectFile = (data: FormData) => {
      const file = data.image[0];
      if (file == null) return;

      const reader = new FileReader();

      reader.addEventListener('load', () => {
        const imageElement = new Image();
        const imageUrl = reader.result?.toString() ?? '';
        imageElement.src = imageUrl;

        imageElement.addEventListener('load', (e) => {
          if (error !== '') setError('');

          const target = e.currentTarget as HTMLImageElement;
          let { naturalWidth, naturalHeight } = target;

          if (naturalWidth < MIN_DIMENSION || naturalHeight < MIN_DIMENSION) {
            const scale = Math.max(MIN_DIMENSION / naturalWidth, MIN_DIMENSION / naturalHeight);
            naturalWidth *= scale;
            naturalHeight *= scale;
            target.width = naturalWidth;
            target.height = naturalHeight;
          }

          setImgSrc(imageUrl);
          setShow(true);
        });
      });
      reader.readAsDataURL(file);
    };

    const onImageLoaded = (e: SyntheticEvent<HTMLImageElement>) => {
      const { width, height } = e.currentTarget;

      const newCrop = makeAspectCrop(
        {
          unit: '%',
          width: 100,
        },
        ASPECT_RATIO,
        width,
        height,
      );
      const centeredCrop = centerCrop(newCrop, width, height);
      setCrop(centeredCrop);
    };

    const closeModal = () => setShow(false);

    const handleCrop = () => {
      if (previewCanvasRef.current != null && imgRef.current != null && crop != null) {
        const pixelCrop = convertToPixelCrop(crop, imgRef.current);
        setCanvasPreview(imgRef.current, previewCanvasRef.current, pixelCrop);

        const dataUrl = previewCanvasRef.current.toDataURL();
        onChange(dataUrl);

        closeModal();
      }
    };

    return (
      <>
        <input
          type="file"
          accept={accept}
          data-testid="image-input"
          className={`${className} block w-full text-sm text-foreground-subtle file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-muted file:text-muted-foreground hover:file:bg-accent`}
          ref={ref}
          // Reset the value on every open so re-selecting the same file (e.g. upload, delete,
          // upload the same image again) still fires onChange.
          onClick={(e) => {
            (e.target as HTMLInputElement).value = '';
          }}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            if (e.target.files != null && e.target.files.length > 0) {
              if (e.target.files[0].size > maxSize) {
                return onError(Error('File too big'));
              }

              onSelectFile({ image: e.target.files });
            }
          }}
        />

        <Dialog open={show} onOpenChange={setShow}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle id="crop-image-dialog">{t('title')}</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              {error !== '' && (
                <div className="p-3 mb-4 text-sm text-red-700 bg-red-100 rounded-lg">{error}</div>
              )}

              {imgSrc !== '' && (
                <div className="flex flex-col items-center">
                  <ReactCrop
                    crop={crop}
                    circularCrop
                    keepSelection
                    aspect={ASPECT_RATIO}
                    onChange={(newCrop) => {
                      setCrop(newCrop);
                    }}
                  >
                    <img
                      ref={imgRef}
                      src={imgSrc}
                      alt="Crop"
                      style={{ width: 'auto', height: 'auto' }}
                      onLoad={onImageLoaded}
                    />
                  </ReactCrop>
                </div>
              )}

              <canvas ref={previewCanvasRef} className="image-input-canvas" />
            </div>
            <DialogFooter className="flex items-center gap-2">
              <Button variant="secondary" onClick={closeModal}>
                {t('common:cancel')}
              </Button>
              <Button onClick={handleCrop}>{t('crop')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  },
);

export default ImageInput;
